import './PortfolioPieChart.scss';
import {Fragment, useEffect, useMemo, useState} from 'react';
import {formatCurrency} from '../../../utils';

// ── Color helpers ─────────────────────────────────────────────────────

const stringToHue = (str) => {
    let hash = 0;
    String(str).split('').forEach(c => { hash = c.charCodeAt(0) + ((hash << 5) - hash); });
    return Math.abs(hash) % 360;
};

const sourceColor = (name) => `hsl(${stringToHue(name)}, 80%, 68%)`;

const accountColor = (sourceName, idx, total) => {
    const l = total <= 1 ? 64 : 52 + (idx / (total - 1)) * 24;
    return `hsl(${stringToHue(sourceName)}, 72%, ${Math.round(l)}%)`;
};

// ── Exchange rate helpers ─────────────────────────────────────────────

const getRate = (currencyRates, currency, todayStr) => {
    if (currency === 'UAH') return 1;
    const sorted = [...currencyRates].sort((a, b) => a.date.localeCompare(b.date));
    const applicable = sorted.reduce(
        (found, r) => (r.date <= todayStr ? r : found), null,
    );
    if (!applicable) return 1;
    if (currency === 'USD') return Number(applicable.uahToUsdRate) || 1;
    if (currency === 'EUR') return Number(applicable.uahToEurRate) || 1;
    return 1;
};

// ── Source grouping ───────────────────────────────────────────────────

const groupBySource = (accounts) => {
    const map = new Map();
    for (const a of accounts) {
        const key = a.sourceName ?? 'Other';
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(a);
    }
    const groups = [];
    for (const [source, items] of map) {
        if (source !== 'Other') groups.push({source, items});
    }
    if (map.has('Other')) groups.push({source: 'Other', items: map.get('Other')});
    return groups;
};

// ── SVG geometry ──────────────────────────────────────────────────────

const donutArc = (cx, cy, r, thickness, startA, endA) => {
    const {cos, sin} = Math;
    const ir = r - thickness;
    const x1 = cx + r * cos(startA);
    const y1 = cy + r * sin(startA);
    const x2 = cx + r * cos(endA);
    const y2 = cy + r * sin(endA);
    const x3 = cx + ir * cos(endA);
    const y3 = cy + ir * sin(endA);
    const x4 = cx + ir * cos(startA);
    const y4 = cy + ir * sin(startA);
    const large = (endA - startA) > Math.PI ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${ir} ${ir} 0 ${large} 0 ${x4} ${y4} Z`;
};

// ── Segment builder ───────────────────────────────────────────────────

const GAP = 0.006;

const buildSegments = (accounts, balancesToday, currencyRates, today) => {
    const nonZero = accounts.filter(a => (balancesToday[a.id] ?? 0) !== 0);
    const groups = groupBySource(nonZero);
    if (!groups.length) return null;

    const groupsWithUAH = groups.map(({source, items}) => ({
        source,
        items: items
            .map(a => ({
                ...a,
                uahValue: Math.abs(balancesToday[a.id]) * getRate(currencyRates, a.currencyCode, today),
            }))
            .filter(a => a.uahValue > 0)
            .sort((a, b) => b.uahValue - a.uahValue),
    })).filter(g => g.items.length > 0);

    const totalPortfolio = groupsWithUAH.reduce(
        (s, g) => s + g.items.reduce((ss, a) => ss + a.uahValue, 0), 0,
    );
    if (!totalPortfolio) return null;

    const numGroups = groupsWithUAH.length;
    const availableAngle = 2 * Math.PI - numGroups * GAP;
    let currentAngle = -Math.PI / 2;
    const segments = [];

    for (const {source, items} of groupsWithUAH) {
        const sourceTotal = items.reduce((s, a) => s + a.uahValue, 0);
        const srcSpan = (sourceTotal / totalPortfolio) * availableAngle;
        const srcStartA = currentAngle;
        const srcEndA = srcStartA + srcSpan;
        const srcId = `source:${source}`;

        segments.push({
            type: 'source', id: srcId, name: source,
            startA: srcStartA, endA: srcEndA,
            color: sourceColor(source),
            uahValue: sourceTotal,
        });

        const numAcctGaps = items.length - 1;
        const acctAvailable = srcSpan - numAcctGaps * (GAP / 2);
        let acctAngle = srcStartA;

        items.forEach((a, i) => {
            const span = (a.uahValue / sourceTotal) * acctAvailable;
            segments.push({
                type: 'account',
                id: `account:${a.id}`,
                accountId: a.id,
                sourceId: srcId,
                name: a.title,
                currencyCode: a.currencyCode,
                balance: balancesToday[a.id],
                uahValue: a.uahValue,
                startA: acctAngle,
                endA: acctAngle + span,
                color: accountColor(source, i, items.length),
            });
            acctAngle += span + (i < items.length - 1 ? GAP / 2 : 0);
        });

        currentAngle = srcEndA + GAP;
    }

    return {
        segments: segments.sort((a, b) => b.uahValue - a.uahValue),
        totalUAH: totalPortfolio
    };
};

// ── Constants ─────────────────────────────────────────────────────────

const CX = 120, CY = 120;
const OUTER_R = 90, OUTER_T = 28;   // accounts ring
const INNER_R = 54, INNER_T = 18;   // sources ring
const ACTIVE_OFFSET = 6;

const pad2 = n => String(n).padStart(2, '0');
const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

// ── Component ─────────────────────────────────────────────────────────

const PortfolioPieChart = ({accounts, balancesToday, currencyRates}) => {
    const [isTouchDevice, setIsTouchDevice] = useState(false);
    const [hoveredId, setHoveredId] = useState(null);
    const [selectedId, setSelectedId] = useState(null);

    useEffect(() => {
        const mq = window.matchMedia('(hover: none), (pointer: coarse)');
        const apply = () => setIsTouchDevice(mq.matches);
        apply();
        if (mq.addEventListener) {
            mq.addEventListener('change', apply);
            return () => mq.removeEventListener('change', apply);
        }
        mq.addListener(apply);
        return () => mq.removeListener(apply);
    }, []);

    const today = useMemo(todayStr, []);
    const {segments, totalUAH} = useMemo(
        () => buildSegments(accounts, balancesToday, currencyRates, today) ?? {segments: null, totalUAH: 0},
        [accounts, balancesToday, currencyRates, today],
    );

    const fmtPct = (uahValue) => {
        if (!totalUAH) return '—';
        const pct = (uahValue / totalUAH) * 100;
        if (pct < 0.1) return '<0.1%';
        return `${pct.toFixed(1)}%`;
    };

    const activeId = isTouchDevice ? selectedId : hoveredId;

    const onEnter = (id) => !isTouchDevice && setHoveredId(id);
    const onLeave = () => !isTouchDevice && setHoveredId(null);
    const onTap = (id) => isTouchDevice && setSelectedId(prev => prev === id ? null : id);

    if (!segments?.length) {
        return (
            <section className="portfolio-chart dashboard__section">
                <div className="dashboard__section-title">Portfolio</div>
                <p className="dashboard__empty">No account data.</p>
            </section>
        );
    }

    const sources = segments.filter(s => s.type === 'source');
    const acctSegs = segments.filter(s => s.type === 'account');

    const isSrcActive = (srcId) => activeId === srcId;
    const isAcctActive = (seg) =>
        activeId === seg.id || activeId === seg.sourceId;

    return (
        <section className="portfolio-chart dashboard__section">
            <div className="dashboard__section-title">Portfolio</div>
            <div className="portfolio-chart__body">

                <div className="portfolio-chart__chart-side">
                    <svg
                        viewBox={"0 0 240 240"}
                        className="portfolio-chart__svg"
                        onMouseLeave={() => !isTouchDevice && setHoveredId(null)}
                    >
                        {acctSegs.map(seg => (
                            <g key={seg.id}>
                                <path
                                    d={donutArc(CX, CY, isAcctActive(seg) ? OUTER_R + ACTIVE_OFFSET : OUTER_R, OUTER_T, seg.startA, seg.endA)}
                                    fill={seg.color}
                                    style={{pointerEvents: 'none'}}
                                />
                                <path
                                    d={donutArc(CX, CY, OUTER_R, OUTER_T, seg.startA, seg.endA)}
                                    fill="transparent"
                                    style={{cursor: 'pointer'}}
                                    onMouseEnter={() => onEnter(seg.id)}
                                    onMouseLeave={onLeave}
                                    onClick={() => onTap(seg.id)}
                                />
                            </g>
                        ))}

                        {sources.map(seg => (
                            <g key={seg.id}>
                                <path
                                    d={donutArc(CX, CY, isSrcActive(seg.id) ? INNER_R + ACTIVE_OFFSET : INNER_R, INNER_T, seg.startA, seg.endA)}
                                    fill={seg.color}
                                    style={{pointerEvents: 'none'}}
                                />
                                <path
                                    d={donutArc(CX, CY, INNER_R, INNER_T, seg.startA, seg.endA)}
                                    fill="transparent"
                                    style={{cursor: 'pointer'}}
                                    onMouseEnter={() => onEnter(seg.id)}
                                    onMouseLeave={onLeave}
                                    onClick={() => onTap(seg.id)}
                                />
                            </g>
                        ))}
                    </svg>
                </div>

                <div className="portfolio-chart__legend-side">
                    <table className="portfolio-chart__legend">
                        <tbody>
                            {sources.map(src => {
                                const srcAccts = acctSegs.filter(a => a.sourceId === src.id);
                                return (
                                    <Fragment key={src.id}>
                                        <tr
                                            className={`portfolio-chart__legend-source${isSrcActive(src.id) ? ' is-active' : ''}`}
                                            onMouseEnter={() => !isTouchDevice && setHoveredId(src.id)}
                                            onMouseLeave={() => !isTouchDevice && setHoveredId(null)}
                                            onClick={() => isTouchDevice && setSelectedId(prev => prev === src.id ? null : src.id)}
                                        >
                                            <td className="portfolio-chart__legend-dot-cell">
                                                <span className="portfolio-chart__legend-dot" style={{background: src.color}}/>
                                            </td>
                                            <td className="portfolio-chart__legend-label portfolio-chart__legend-source-label">
                                                {src.name}
                                            </td>
                                            <td className="portfolio-chart__legend-pct portfolio-chart__legend-source-pct">
                                                {fmtPct(src.uahValue)}
                                            </td>
                                            <td colSpan={2}/>
                                        </tr>
                                        {srcAccts.map(acct => (
                                            <tr
                                                key={acct.id}
                                                className={`portfolio-chart__legend-account${isAcctActive(acct) ? ' is-active' : ''}`}
                                                onMouseEnter={() => !isTouchDevice && setHoveredId(acct.id)}
                                                onMouseLeave={() => !isTouchDevice && setHoveredId(null)}
                                                onClick={() => isTouchDevice && setSelectedId(prev => prev === acct.id ? null : acct.id)}
                                            >
                                                <td className="portfolio-chart__legend-dot-cell">
                                                    <span className="portfolio-chart__legend-dot" style={{background: acct.color}}/>
                                                </td>
                                                <td className="portfolio-chart__legend-label">{acct.name}</td>
                                                <td className="portfolio-chart__legend-pct">{fmtPct(acct.uahValue)}</td>
                                                <td className="portfolio-chart__legend-currency">{acct.currencyCode}</td>
                                                <td className="portfolio-chart__legend-amount">
                                                    {formatCurrency(acct.balance, acct.currencyCode)}
                                                </td>
                                            </tr>
                                        ))}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

            </div>
        </section>
    );
};

export default PortfolioPieChart;
